import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProgressOverview from './components/ProgressOverview';
import CourseProgressCard from './components/CourseProgressCard';
import AchievementCard from './components/AchievementCard';
import LearningGoalCard from './components/LearningGoalCard';
import LearningCalendar from './components/LearningCalendar';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseAndModules } from '../../hooks/useCourseData';
import { useUserProgress } from '../../hooks/useUserProgress';
import { DEFAULT_COURSE_SLUG } from '../../lib/supabase';

const UserProgressDashboard = () => {
  const [activeTab, setActiveTab] = useState('current');
  const [sortBy, setSortBy] = useState('recent');
  const [filterBy, setFilterBy] = useState('all');
  const { user, session } = useAuth();
  const { course } = useCourseAndModules(DEFAULT_COURSE_SLUG);
  const { progress } = useUserProgress(
    session?.systemUser?.id,
    user?.id,
    course?.id ? String(course.id) : undefined
  );

  const overviewData = {
    totalCourses: progress?.isEnrolled ? 1 : 0,
    completionPercentage: progress?.progressPercentage ?? 0,
    learningStreak: 0,
    timeInvested: "Indisponível"
  };

  const canonicalCourse = course && progress ? [{
    id: course.id,
    title: course.title,
    description: course.summary || '',
    thumbnail: course.cover || '/assets/images/no_image.png',
    progress: progress.progressPercentage,
    completedLessons: progress.completedLessons,
    totalLessons: progress.totalLessons,
    lastAccessed: progress.lastAccessedAt,
    nextLesson: progress.nextLesson?.title,
  }] : [];
  const currentCourses = canonicalCourse.filter(() => (progress?.progressPercentage ?? 0) < 100);
  const completedCourses = canonicalCourse.filter(() => progress?.progressPercentage === 100);
  const achievements = [];

  // Mock data for learning goals
  const [learningGoals, setLearningGoals] = useState([]);
  const activityData = [];

  const tabs = [
    { id: 'current', label: 'Cursos Atuais', icon: 'BookOpen' },
    { id: 'completed', label: 'Concluídos', icon: 'CheckCircle' },
    { id: 'achievements', label: 'Conquistas', icon: 'Trophy' },
    { id: 'goals', label: 'Metas', icon: 'Target' }
  ];

  const handleUpdateGoal = (updatedGoal) => {
    setLearningGoals(prev => 
      prev?.map(goal => goal?.id === updatedGoal?.id ? updatedGoal : goal)
    );
  };

  const handleDeleteGoal = (goalId) => {
    setLearningGoals(prev => prev?.filter(goal => goal?.id !== goalId));
  };

  const handleExportProgress = () => {
    // Mock export functionality
    console.log('Exporting progress report...');
  };

  const getFilteredContent = () => {
    switch (activeTab) {
      case 'current':
        return currentCourses;
      case 'completed':
        return completedCourses;
      case 'achievements':
        return achievements;
      case 'goals':
        return learningGoals;
      default:
        return [];
    }
  };

  const renderTabContent = () => {
    const content = getFilteredContent();

    switch (activeTab) {
      case 'current':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {content?.map((course) => (
              <CourseProgressCard key={course?.id} course={course} type="current" />
            ))}
          </div>
        );
      
      case 'completed':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {content?.map((course) => (
              <CourseProgressCard key={course?.id} course={course} type="completed" />
            ))}
          </div>
        );
      
      case 'achievements':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {content?.map((achievement) => (
              <AchievementCard key={achievement?.id} achievement={achievement} />
            ))}
          </div>
        );
      
      case 'goals':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {content?.map((goal) => (
              <LearningGoalCard 
                key={goal?.id} 
                goal={goal} 
                onUpdate={handleUpdateGoal}
                onDelete={handleDeleteGoal}
              />
            ))}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Meu Progresso
              </h1>
              <p className="text-muted-foreground font-caption">
                Acompanhe seu desenvolvimento educacional e conquistas
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={handleExportProgress}
                iconName="Download"
                iconPosition="left"
                iconSize={16}
              >
                Exportar Relatório
              </Button>
            </div>
          </div>

          {/* Progress Overview */}
          <ProgressOverview overviewData={overviewData} />

          {/* Main Content Area */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Tabs and Content */}
            <div className="xl:col-span-2 space-y-6">
              {/* Tab Navigation */}
              <div className="bg-card rounded-lg border border-border p-2">
                <div className="flex flex-wrap gap-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-micro ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Filters and Sort */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Filter" size={16} className="text-muted-foreground" />
                    <select
                      value={filterBy}
                      onChange={(e) => setFilterBy(e?.target?.value)}
                      className="text-sm border border-border rounded-lg px-3 py-2 bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="all">Todos</option>
                      <option value="in-progress">Em Progresso</option>
                      <option value="completed">Concluídos</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e?.target?.value)}
                      className="text-sm border border-border rounded-lg px-3 py-2 bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="recent">Mais Recente</option>
                      <option value="progress">Progresso</option>
                      <option value="alphabetical">Alfabética</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-sm text-muted-foreground font-caption">
                  <Icon name="BarChart3" size={16} />
                  <span>{getFilteredContent()?.length} itens</span>
                </div>
              </div>

              {/* Tab Content */}
              <div className="min-h-[400px]">
                {renderTabContent()}
              </div>
            </div>

            {/* Right Column - Calendar */}
            <div className="xl:col-span-1">
              <LearningCalendar activityData={activityData} />
            </div>
          </div>
        </div>
      </main>
      <FloatingAIChat />
    </div>
  );
};

export default UserProgressDashboard;
